import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useCallback, useEffect, useState, type ReactNode } from 'react'
import { useNavigate, useParams } from 'react-router'

import {
  acceptPickup,
  declineOffer,
  fetchMedicationRequest,
  medicationRequestKey,
} from '../api/medications'
import { requestOtp, verifyOtp, type PatientSession } from '../api/patientAuth'
import { Icon } from '../components/Icon'
import {
  PAGE_GUTTER_DESKTOP,
  PageBand,
  PageShell,
} from '../components/PageShell'
import { ApiError } from '../lib/api'
import { getPatientSession, setPatientSession } from '../lib/patientSession'
import { directionsUrl } from '../lib/hospitalProfile'
import { formatPesewas } from '../lib/money'

const inputCls =
  'w-full rounded-r3 border border-line-2 bg-bg-app px-3 py-2 text-sm text-ink-1 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent'

/**
 * Medicine offer (M12-T06b, ADR-0028) at `/medicines/offer/:reference`. The
 * patient verifies their phone (the SMS deep-link lands here), then sees either
 * the waiting state or the **one** curated offer — pharmacy + medicine price +
 * **pickup** (bring prescription, pay at the pharmacy = off-gateway, ADR-0017).
 * Accept marks it accepted; decline returns the request to AMTC for the
 * next-best. No list of pharmacy answers is ever shown.
 */
export function MedicineOffer(): ReactNode {
  const { reference = '' } = useParams()
  // Reuse the session from the just-completed request (in-memory only). A cold
  // visit — reload, new tab, forwarded SMS link — starts null and re-verifies.
  const [session, setSession] = useState<PatientSession | null>(() =>
    getPatientSession(),
  )

  const verified = (s: PatientSession) => {
    setPatientSession(s)
    setSession(s)
  }
  // The cached token expired (401) → drop back to the verify gate.
  const authExpired = useCallback(() => {
    setPatientSession(null)
    setSession(null)
  }, [])

  return (
    <PageShell measure={640}>
      <Header />
      {/* One band for the body: everything nested inherits the 640px measure
          through context and no padding value moves, so mobile is unchanged
          by construction. */}
      <PageBand
        className="flex flex-1 flex-col"
        contentClassName="flex flex-1 flex-col"
      >
        {session === null ? (
          <VerifyGate onVerified={verified} />
        ) : (
          <Offer
            token={session.token}
            reference={reference}
            onAuthExpired={authExpired}
          />
        )}
      </PageBand>
    </PageShell>
  )
}

function Header(): ReactNode {
  const navigate = useNavigate()
  return (
    <header
      className={`flex items-center gap-1.5 border-b border-line-1 px-3 py-2 ${PAGE_GUTTER_DESKTOP}`}
    >
      <button
        type="button"
        aria-label="Back"
        onClick={() => navigate(-1)}
        className="rounded p-1.5 text-ink-2 hover:bg-bg-soft focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent"
      >
        <Icon name="chevl" size={18} />
      </button>
      <span className="text-[13px] font-medium text-ink-1">Your offer</span>
    </header>
  )
}

/** Phone OTP gate — the patient token lives only in memory, so viewing an offer
 * (from the SMS link) re-verifies the phone. */
function VerifyGate({
  onVerified,
}: {
  onVerified: (s: PatientSession) => void
}): ReactNode {
  const [phone, setPhone] = useState('')
  const [code, setCode] = useState('')
  const [codeSent, setCodeSent] = useState(false)

  const requestMutation = useMutation({
    mutationFn: () => requestOtp(phone.trim()),
    onSuccess: () => setCodeSent(true),
  })
  const verifyMutation = useMutation({
    mutationFn: () => verifyOtp(phone.trim(), code.trim()),
    onSuccess: onVerified,
  })

  return (
    <div className="flex flex-1 flex-col gap-3 px-4 py-5">
      <div>
        <h1 className="text-base font-bold text-ink-1">Verify your phone</h1>
        <p className="mt-0.5 text-[13px] text-ink-3">
          Enter the number you used to request the medicine to see your offer.
        </p>
      </div>
      <div className="flex gap-2">
        <input
          type="tel"
          aria-label="Phone number"
          placeholder="Phone, e.g. +233…"
          value={phone}
          onChange={(e) => setPhone(e.target.value)}
          className={inputCls}
        />
        <button
          type="button"
          disabled={phone.trim().length < 8 || requestMutation.isPending}
          onClick={() => requestMutation.mutate()}
          className="shrink-0 rounded-r3 border border-accent bg-accent-bg px-3 text-[13px] font-medium text-accent-ink disabled:opacity-50 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent"
        >
          {requestMutation.isPending
            ? 'Sending…'
            : codeSent
              ? 'Resend'
              : 'Send code'}
        </button>
      </div>
      {requestMutation.isError && (
        <p role="alert" className="text-[12px] text-bed-bad">
          {errMsg(requestMutation.error)}
        </p>
      )}
      {codeSent && (
        <div className="flex gap-2">
          <input
            type="text"
            inputMode="numeric"
            aria-label="Verification code"
            placeholder="6-digit code"
            value={code}
            onChange={(e) => setCode(e.target.value)}
            className={inputCls}
          />
          <button
            type="button"
            disabled={code.trim().length < 4 || verifyMutation.isPending}
            onClick={() => verifyMutation.mutate()}
            className="shrink-0 rounded-r3 bg-accent px-4 text-[13px] font-medium text-accent-on disabled:opacity-50 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent"
          >
            Verify
          </button>
        </div>
      )}
      {verifyMutation.isError && (
        <p role="alert" className="text-[12px] text-bed-bad">
          {errMsg(verifyMutation.error)}
        </p>
      )}
    </div>
  )
}

function Offer({
  token,
  reference,
  onAuthExpired,
}: {
  token: string
  reference: string
  onAuthExpired: () => void
}): ReactNode {
  const queryClient = useQueryClient()
  const query = useQuery({
    queryKey: medicationRequestKey(reference),
    queryFn: ({ signal }) => fetchMedicationRequest(token, reference, signal),
    retry: (count, err) =>
      err instanceof ApiError &&
      (err.status === 401 || err.code === 'not_found')
        ? false
        : count < 2,
  })

  // A stale cached token → re-verify rather than show a misleading error.
  useEffect(() => {
    if (query.error instanceof ApiError && query.error.status === 401) {
      onAuthExpired()
    }
  }, [query.error, onAuthExpired])

  const acceptMutation = useMutation({
    mutationFn: () => acceptPickup(token, reference),
    onSuccess: () =>
      void queryClient.invalidateQueries({
        queryKey: medicationRequestKey(reference),
      }),
  })
  const declineMutation = useMutation({
    mutationFn: () => declineOffer(token, reference),
    onSuccess: () =>
      void queryClient.invalidateQueries({
        queryKey: medicationRequestKey(reference),
      }),
  })

  if (query.isPending) {
    return (
      <p
        className="px-4 py-12 text-center text-[13px] text-ink-3"
        role="status"
      >
        Loading…
      </p>
    )
  }
  if (query.isError) {
    return (
      <div className="px-4 py-12 text-center">
        <p role="alert" className="text-sm font-medium text-bed-bad">
          {query.error instanceof ApiError && query.error.code === 'not_found'
            ? 'Request not found for this phone.'
            : "Couldn't load your offer."}
        </p>
      </div>
    )
  }

  const req = query.data
  const drug = `${req.drug_generic_name} ${req.drug_strength} · ${req.quantity}`

  // Accepted → done.
  if (req.offer && req.offer.status === 'accepted') {
    return (
      <div className="flex flex-1 flex-col items-center justify-center gap-2 px-6 py-16 text-center">
        <span className="grid h-12 w-12 place-items-center rounded-full bg-bed-good-bg text-bed-good">
          <Icon name="check" size={22} />
        </span>
        <h1 className="font-display text-d-xs font-medium tracking-display text-ink-1">
          Pickup confirmed
        </h1>
        <p className="text-[13px] text-ink-3">
          Bring your prescription to {req.offer.hospital_name} and pay{' '}
          {formatPesewas(req.offer.medicine_price_pesewas)} at the pharmacy.
        </p>
        <a
          href={directionsUrl(req.offer.hospital_lat, req.offer.hospital_lng)}
          target="_blank"
          rel="noreferrer"
          className="mt-2 inline-flex items-center gap-1.5 rounded-r3 bg-accent px-4 py-2 text-sm font-medium text-accent-on focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent"
        >
          <Icon name="arrow" size={15} /> Get directions
        </a>
      </div>
    )
  }

  // No offer yet → waiting.
  if (!req.offer) {
    return (
      <div className="flex flex-1 flex-col items-center justify-center gap-2 px-6 py-16 text-center">
        <span className="grid h-12 w-12 animate-pulse place-items-center rounded-full bg-accent-bg text-accent-ink">
          <Icon name="clock" size={22} />
        </span>
        <h1 className="text-base font-semibold text-ink-1">
          Finding your best offer
        </h1>
        <p className="text-[13px] text-ink-3">
          {/* M34-T08: no reply time (the no-wait rule). */}
          We asked all 10 partner pharmacies, and we&apos;ll text you when an
          offer is ready. You can check back here any time.
        </p>
      </div>
    )
  }

  const offer = req.offer
  return (
    <div className="flex flex-1 flex-col gap-3 px-4 py-4">
      <p className="text-[12px] text-ink-3">{drug}</p>
      <div className="flex items-center gap-2 rounded-r3 border border-line-1 bg-bg-soft p-3 text-[12.5px] text-ink-2">
        <Icon name="badge" size={16} className="text-accent-ink" />
        The AMTC concierge checked 10 pharmacies and picked the best one for
        you.
      </div>

      <div className="rounded-xl border border-line-2 p-3.5">
        <h2 className="text-[15px] font-bold text-ink-1">
          {offer.hospital_name}
        </h2>
        <p className="mt-0.5 inline-flex items-center gap-1 text-[11.5px] text-ink-3">
          <Icon name="pin" size={11} /> {offer.city}
        </p>
        <div className="mt-2.5 flex items-center justify-between border-t border-line-1 pt-2.5 text-[13px]">
          <span className="text-ink-1">{drug}</span>
          <span className="font-semibold tabular-nums text-ink-1">
            {formatPesewas(offer.medicine_price_pesewas)}
          </span>
        </div>

        <div className="mt-3 rounded-r3 border border-accent bg-accent-bg p-3">
          <div className="flex items-start gap-2">
            <Icon name="pin" size={16} className="text-accent-ink" />
            <div>
              <p className="text-[13px] font-semibold text-ink-1">
                Pick up at the pharmacy
              </p>
              <p className="text-[11.5px] text-ink-2">
                Bring your prescription · pay{' '}
                {formatPesewas(offer.medicine_price_pesewas)} at the pharmacy
              </p>
            </div>
          </div>
        </div>

        <div className="mt-3 flex gap-2">
          <a
            href={directionsUrl(offer.hospital_lat, offer.hospital_lng)}
            target="_blank"
            rel="noreferrer"
            className="flex flex-1 items-center justify-center gap-1.5 rounded-r3 border border-line-2 bg-bg-app px-4 py-2.5 text-sm font-medium text-ink-1 hover:bg-bg-soft focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent"
          >
            <Icon name="arrow" size={15} /> Directions
          </a>
          <button
            type="button"
            disabled={acceptMutation.isPending}
            onClick={() => acceptMutation.mutate()}
            className="flex flex-1 items-center justify-center gap-1.5 rounded-r3 bg-accent px-4 py-2.5 text-sm font-medium text-accent-on disabled:opacity-50 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent"
          >
            {acceptMutation.isPending ? 'Confirming…' : 'Accept pickup'}
          </button>
        </div>
        <p className="mt-2 text-center text-[10.5px] text-ink-3">
          Nothing to pay online — the pharmacist checks your prescription and
          takes payment at pickup.
        </p>
      </div>

      <button
        type="button"
        disabled={declineMutation.isPending}
        onClick={() => declineMutation.mutate()}
        className="mx-auto text-[12px] text-ink-3 underline hover:text-ink-1 disabled:opacity-50 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent"
      >
        This doesn&apos;t work for me — ask AMTC for another option
      </button>
      {(acceptMutation.isError || declineMutation.isError) && (
        <p role="alert" className="text-center text-[12px] text-bed-bad">
          {errMsg(acceptMutation.error ?? declineMutation.error)}
        </p>
      )}
    </div>
  )
}

function errMsg(err: unknown): string {
  if (err instanceof ApiError) {
    if (err.code === 'rate_limited')
      return 'Too many requests. Please try again later.'
    return err.message
  }
  return 'Something went wrong. Please try again.'
}
